import React, { useRef, useState } from 'react';
import { Box, Typography, makeStyles } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { useTranslation } from '@michelin/central-provider';

const useStyles = makeStyles((theme) => ({
  filePickerContainer: {
    marginBottom: theme.spacing(3),
  },
  dropZone: {
    border: `2px dashed ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(4),
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    backgroundColor: theme.palette.background.default,
    '&:hover': {
      borderColor: theme.palette.primary.main,
      backgroundColor: theme.palette.action.hover,
    },
    '&.drag-over': {
      borderColor: theme.palette.primary.main,
      backgroundColor: theme.palette.action.selected,
    },
  },
  fileInput: {
    display: 'none',
  },
  fileName: {
    marginTop: theme.spacing(2),
    wordBreak: 'break-word',
    maxWidth: '100%',
  },
}));

interface FileUploadZoneProps {
  file: File | null;
  fileKey: number;
  fileError: string;
  onFileSelected: (file: File | null) => void;
}

export const FileUploadZone: React.FC<FileUploadZoneProps> = ({ file, fileKey, fileError, onFileSelected }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  const handleFilePick = (event: React.ChangeEvent<HTMLInputElement>) => {
    onFileSelected(event.target.files?.[0] || null);
  };
  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragOver(false);
    onFileSelected(event.dataTransfer.files?.[0] || null);
  };
  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragOver(true);
  };
  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragOver(false);
  };
  const handleDropZoneClick = () => {
    inputRef.current?.click();
  };

  return (
    <Box className={classes.filePickerContainer}>
      <input
        ref={inputRef}
        key={fileKey}
        accept=".xlsx"
        className={classes.fileInput}
        type="file"
        onChange={handleFilePick}
      />
      <Box
        className={`${classes.dropZone} ${isDragOver ? 'drag-over' : ''}`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={handleDropZoneClick}
      >
        {!file ? (
          <>
            <Typography variant="body1" gutterBottom>
              {t('Drag & Drop File or Click to Browse')}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {t('Please ensure your file is less than 60KB in size.')}
            </Typography>
          </>
        ) : (
          <>
            <Typography className={classes.fileName} variant="body1">
              <strong>{file.name}</strong>
            </Typography>
            <Typography variant="body2" color="textSecondary" style={{ marginTop: 16 }}>
              {t('Click to replace file')}
            </Typography>
          </>
        )}
      </Box>
      {fileError && (
        <Alert severity="error" style={{ marginTop: 16 }}>
          {fileError}
        </Alert>
      )}
    </Box>
  );
};
